<template>
  <!-- Contenedor principal con ancho máximo y centrado -->
  <div class="max-w-5xl mx-auto px-4">
    <div class="flex flex-col gap-3 mt-5">
      <div class="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 class="text-4xl font-extrabold text-gray-800 tracking-tight">
            <span class="inline-block text-orange-500">Calendario</span> de Citas
          </h1>
          <p class="text-sm text-gray-500">
            Gestiona el flujo de citas y bloquea tiempos personales del equipo.
          </p>
        </div>
        <div class="flex gap-2">
          <button
            @click="mostrarModalCrear = true"
            class="bg-orange-600 text-white px-4 py-2 rounded shadow hover:bg-orange-700 transition"
          >
            ➕ Crear nueva cita
          </button>
          <button
            @click="abrirModalPersonal"
            :disabled="!Object.keys(enfermeros).length"
            class="bg-amber-500 text-white px-4 py-2 rounded shadow hover:bg-amber-600 transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            ⭐ Registrar asunto personal
          </button>
        </div>
    </div>

    <!-- Modal para asuntos personales -->
    <div
      v-if="mostrarModalPersonal"
      class="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50"
    >
      <div
        class="bg-white rounded-lg p-6 w-full max-w-md mx-4 shadow-lg space-y-4"
      >
        <div class="flex items-start justify-between">
          <div>
            <h5 class="text-lg font-semibold text-gray-800">
              Asunto personal
            </h5>
            <p class="text-sm text-gray-500">
              Bloquea tiempo interno sin involucrar pacientes.
            </p>
          </div>
          <button
            @click="cerrarModalPersonal"
            class="text-gray-500 hover:text-gray-700 text-2xl leading-3"
          >
            &times;
          </button>
        </div>

        <form @submit.prevent="crearAsuntoPersonal" class="space-y-4">
          <div>
            <label class="text-sm font-semibold text-gray-700 block mb-1">
              Título
            </label>
            <input
              v-model="formPersonal.titulo"
              type="text"
              required
              placeholder="Ej. Buscar a los niños al colegio"
              class="w-full border border-gray-300 rounded-lg p-2 focus:ring-2 focus:ring-amber-500 focus:outline-none"
            />
          </div>
          <div>
            <label class="text-sm font-semibold text-gray-700 block mb-1">
              Fecha
            </label>
            <input
              v-model="formPersonal.fecha"
              type="date"
              required
              class="w-full border border-gray-300 rounded-lg p-2 focus:ring-2 focus:ring-amber-500 focus:outline-none"
            />
          </div>
          <div class="grid grid-cols-2 gap-3">
            <div>
              <label class="text-sm font-semibold text-gray-700 block mb-1">
                Hora inicio
              </label>
              <TimePicker
                v-model="formPersonal.hora"
                placeholder="Seleccionar hora"
              />
            </div>
            <div>
              <label class="text-sm font-semibold text-gray-700 block mb-1">
                Duración (min)
              </label>
              <input
                v-model.number="formPersonal.duracion"
                type="number"
                min="5"
                required
                class="w-full border border-gray-300 rounded-lg p-2 focus:ring-2 focus:ring-amber-500 focus:outline-none"
              />
            </div>
          </div>
          <div>
            <label class="text-sm font-semibold text-gray-700 block mb-1">
              Responsable
            </label>
            <select
              v-model="formPersonal.responsable"
              required
              class="w-full border border-gray-300 rounded-lg p-2 focus:ring-2 focus:ring-amber-500 focus:outline-none"
            >
              <option
                v-for="(nombre, uid) in enfermeros"
                :key="uid"
                :value="uid"
              >
                {{ nombre }}
              </option>
            </select>
          </div>
          <div>
            <label class="text-sm font-semibold text-gray-700 block mb-1">
              Nota
            </label>
            <textarea
              v-model="formPersonal.descripcion"
              rows="3"
              placeholder="¿Qué debe recordar el horario personal?"
              class="w-full border border-gray-300 rounded-lg p-2 focus:ring-2 focus:ring-amber-500 focus:outline-none"
            ></textarea>
          </div>
          <div class="flex justify-end gap-2">
            <button
              type="button"
              @click="cerrarModalPersonal"
              class="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 transition"
            >
              Cancelar
            </button>
            <button
              type="submit"
              class="px-4 py-2 bg-amber-500 text-white rounded hover:bg-amber-600 transition"
            >
              Guardar asunto
            </button>
          </div>
        </form>
      </div>
    </div>

      <CreateCita
        v-if="mostrarModalCrear"
        @cerrar="mostrarModalCrear = false"
        @cita-creada="onCitaCreada"
      />


      <!-- Filtro por enfermero -->
      <div class="max-w-md mx-auto w-full mb-6">
        <label
          for="enfermeroSelect"
          class="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
        >
          Filtrar por Esteticistas:
        </label>
        <select
          v-model="selectedEnfermero"
          @change="filtrarCitas"
          class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
          id="enfermeroSelect"
        >
          <option value="">Todos los Esteticistas</option>
          <option v-for="(nombre, id) in enfermeros" :key="id" :value="id">
            {{ nombre }}
          </option>
        </select>
      </div>

      
      <!-- Contenedor del calendario con tamaño fijo -->
      <div class="calendar-container">
        <FullCalendar :options="calendarOptions" />
      </div>

      <!-- Panel lateral para mostrar citas del día -->
      <div v-if="mostrarModal" class="fixed inset-0 z-40">
        <div class="absolute inset-0 bg-black/30" @click.self="cerrarModal"></div>
        <aside class="absolute right-0 top-0 h-full w-full max-w-md bg-white shadow-xl flex flex-col">
          <div class="flex items-center justify-between px-4 py-4 border-b border-gray-200">
            <div>
              <p class="text-xs uppercase font-semibold text-gray-400">
                Citas del día
              </p>
              <h5 class="text-lg font-bold text-gray-800">
                {{ modalFecha }}
              </h5>
              <p class="text-xs text-gray-500">
                {{ citasDelDia.length }} cita(s) programadas
              </p>
            </div>
            <button
              @click="cerrarModal"
              class="text-gray-500 hover:text-gray-700 text-2xl leading-3"
              aria-label="Cerrar"
            >
              &times;
            </button>
          </div>

          <div class="flex-1 overflow-y-auto px-4 py-4">
            <div v-if="modalModo === 'vista'">
              <div v-if="!citasDelDia.length" class="text-sm text-gray-500 bg-gray-50 border border-dashed border-gray-200 rounded-lg p-4">
                No hay citas para este día.
              </div>

              <div v-else class="space-y-3">
                <article
                  v-for="cita in citasDelDia"
                  :key="cita.id"
                  class="border border-gray-200 rounded-lg overflow-hidden"
                >
                  <button
                    class="w-full text-left p-3 flex items-center justify-between gap-3"
                    @click="toggleCitaDetalle(cita.id)"
                  >
                    <div>
                      <p class="font-semibold text-gray-900">
                        {{ cita.title }}
                      </p>
                      <p class="text-xs text-gray-500">
                        Horario:
                        <span v-if="cita.start">{{ formatDate(cita.start) }}</span>
                        <span v-if="cita.hora"> - {{ cita.hora }}</span>
                        <span v-if="cita.horaFin"> a {{ cita.horaFin }}</span>
                      </p>
                    </div>
                    <span
                      class="text-xs font-semibold px-2 py-1 rounded-full"
                      :class="claseEstadoCita(cita)"
                    >
                      {{ etiquetaEstadoCita(cita) }}
                    </span>
                  </button>

                  <div v-if="citaExpandidaId === cita.id" class="border-t border-gray-100 p-3 space-y-2 text-sm">
                    <div v-if="esCitaPersonal(cita)" class="space-y-2">
                      <p>
                        <span class="font-semibold text-gray-700">Asunto:</span>
                        <span class="text-gray-900 font-semibold">{{ cita.title }}</span>
                      </p>
                      <p v-if="cita.notaPersonal">
                        <span class="font-semibold text-gray-700">Nota:</span>
                        {{ cita.notaPersonal }}
                      </p>
                      <p>
                        <span class="font-semibold text-gray-700">Responsable:</span>
                        {{ cita.enfermero || "Sin asignar" }}
                      </p>
                    </div>
                    <div v-else class="space-y-2">
                      <p>
                        <span class="font-semibold text-gray-700">Paciente:</span>
                        <span
                          :class="{
                            'text-green-600 font-semibold': cita.confirmacionCliente,
                            'text-gray-500 italic': !cita.confirmacionCliente,
                          }"
                        >
                          {{ cita.title }}
                        </span>
                      </p>
                      <p>
                        <span class="font-semibold text-gray-700">Teléfono:</span>
                        {{ cita.telefono }}
                      </p>
                      <p>
                        <span class="font-semibold text-gray-700">Email:</span>
                        {{ cita.email }}
                      </p>
                      <p>
                        <span class="font-semibold text-gray-700">Esteticista:</span>
                        {{ cita.enfermero || "Sin asignar" }}
                      </p>
                      <div>
                        <p class="font-semibold text-gray-700">Servicios:</p>
                        <ul v-if="cita.servicios && cita.servicios.length" class="list-disc list-inside text-gray-600">
                          <li v-for="servicio in cita.servicios" :key="servicio.id">
                            {{ servicio.nombre }} — {{ servicio.duracion || "??" }} min
                          </li>
                        </ul>
                        <p v-else class="text-gray-500">No hay servicios</p>
                      </div>
                      <p>
                        <span class="font-semibold text-gray-700">Confirmación cliente:</span>
                        <span :class="cita.confirmacionCliente ? 'text-green-600' : 'text-amber-600'">
                          {{ cita.confirmacionCliente ? "confirmada" : "pendiente" }}
                        </span>
                      </p>
                    </div>

                    <div v-if="cita.estado === 'pendiente'" class="mt-2 flex flex-wrap gap-2">
                      <button
                        @click="aprobarCita(cita)"
                        class="bg-green-600 text-white px-3 py-1 rounded text-xs"
                      >
                        Aprobar
                      </button>
                      <button
                        @click="denegarCita(cita)"
                        class="bg-red-600 text-white px-3 py-1 rounded text-xs"
                      >
                        Denegar
                      </button>
                    </div>

                    <div class="mt-2 flex flex-wrap gap-2">
                      <button
                        v-if="!cita.confirmacionCliente"
                        @click="confirmarManualCliente(cita)"
                        class="bg-emerald-600 text-white px-3 py-1 rounded text-xs"
                      >
                        Confirmar manual
                      </button>
                      <button
                        v-if="!cita.confirmacionCliente"
                        @click="enviarRecordatorioCliente(cita)"
                        class="bg-amber-500 text-white px-3 py-1 rounded text-xs"
                      >
                        Recordatorio
                      </button>
                      <button
                        @click="cambiarModo('edicion', cita)"
                        class="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded text-xs"
                      >
                        Editar
                      </button>
                      <button
                        @click="eliminarCita(cita.id)"
                        class="bg-red-500 hover:bg-red-600 text-white px-3 py-1 rounded text-xs"
                      >
                        Eliminar
                      </button>
                    </div>
                  </div>
                </article>
              </div>
            </div>

            <div v-else>
              <form
                @submit.prevent="guardarCambios"
                class="space-y-4"
              >
              <div v-if="esCitaPersonal(citaSeleccionada)" class="mb-4">
                <label
                  class="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
                  >Asunto personal</label
                >
                <input
                  v-model="formCita.titulo"
                  type="text"
                  class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-primary-600 focus:border-primary-600 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-primary-500 dark:focus:border-primary-500"
                  placeholder="Ej: Buscar a los niños a la escuela"
                  required
                />
              </div>
              <div v-if="esCitaPersonal(citaSeleccionada)" class="mb-4">
                <label
                  class="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
                  >Nota</label
                >
                <textarea
                  v-model="formCita.notaPersonal"
                  rows="3"
                  class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-primary-600 focus:border-primary-600 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-primary-500 dark:focus:border-primary-500"
                  placeholder="Detalle interno para este asunto"
                ></textarea>
              </div>
              <div class="mb-4">
                <label
                  class="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
                  >Fecha de la Cita</label
                >
                <input
                  v-model="formCita.fechaCita"
                  type="date"
                  class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-primary-600 focus:border-primary-600 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-primary-500 dark:focus:border-primary-500"
                  required
                />
              </div>
              <div class="mb-4">
                <label class="block text-sm font-medium">Hora:</label>
                <TimePicker
                  v-model="formCita.hora"
                  placeholder="Seleccionar hora"
                />
              </div>
              <div class="mb-4">
                <label
                  class="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
                  >Duración (minutos)</label
                >
                <input
                  v-model="formCita.duracion"
                  @change="recalcularDisponibilidad"
                  type="number"
                  min="5"
                  step="5"
                  class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-primary-600 focus:border-primary-600 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-primary-500 dark:focus:border-primary-500"
                  placeholder="Ej: 60"
                  required
                />
              </div>

              <div class="mb-4">
                <label
                  class="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
                  >Hora Fin (automática)</label
                >
                <input
                  :value="citaSeleccionada.horaFin"
                  type="text"
                  disabled
                  class="bg-gray-100 border border-gray-300 text-gray-500 text-sm rounded-lg block w-full p-2.5 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-400"
                />
              </div>

              <div class="mb-4">
                <label class="block text-sm font-medium">Esteticista:</label>
                <select
                  v-model="formCita.enfermero"
                  class="w-full border rounded p-2"
                >
                  <option
                    v-for="(nombre, id) in enfermeros"
                    :key="id"
                    :value="id"
                  >
                    {{ nombre }}
                  </option>
                </select>
              </div>

              <!-- Espaciado entre los campos y los botones -->
              <div class="mt-6 flex justify-end space-x-4">
                <button
                  type="submit"
                  class="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded"
                >
                  Guardar
                </button>
                <button
                  @click="cambiarModo('vista')"
                  type="button"
                  class="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded"
                >
                  Cancelar
                </button>
              </div>
              </form>
            </div>
          </div>
        </aside>
      </div>
    </div>
  </div>
</template>

<script>
import FullCalendar from "@fullcalendar/vue3";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import listPlugin from "@fullcalendar/list";
import interactionPlugin from "@fullcalendar/interaction";
import {
  collection,
  getDocs,
  doc,
  updateDoc,
  addDoc,
  getDoc,
  deleteDoc,
  Timestamp,
  setDoc,
} from "firebase/firestore";
import { db } from "../../firebase";
import TimePicker from "../views/paciente/TimePicker.vue";
import { getFunctions, httpsCallable } from "firebase/functions";
import { obtenerHorariosDisponibles } from "@/utils/horarios";
import { sumarMinutos, generarTimeSlots } from "@/utils/timeUtils";
import { formatIsoPrTime, formatIsoPrDateKey } from "@/utils/prTime";
import { generarPDFCitaDepositoBase64 } from "@/utils/reciboPdf";
import { crearProductoDeposito, registrarDepositoEnVentas } from "@/utils/posHelpers";

import { enviarCorreoRecibo } from "@/utils/enviarCorreoRecibo";
import { obtenerHorarioBaseDesdeFirestore } from "@/utils/horariosService";
import CreateCita from "@/components/views/citas/CreateCita.vue";

export default {
  name: "CalendarioView",
  components: {
    FullCalendar,
    TimePicker,
    CreateCita,
  },
  data() {
    return {
      citas: [],
      citasFiltradas: [],
      enfermeros: [],
      selectedEnfermero: "",
      citasDelDia: [],
      horasViablesParaCita: [],
      modalFecha: "",
      mostrarModal: false,
      mostrarModalCrear: false,
      mostrarModalPersonal: false,
      modalModo: "vista", // vista o edicion
      citaExpandidaId: null,
      formCita: {
        fechaCita: "",
        hora: "",
        enfermero: "",
        duracion: 60,
        titulo: "",
        notaPersonal: "",
      },
      enfermerosLista: [],
      horariosEventos: [],
      intervalosEventos: [],
      coloresHorarios: {},
      paletaColoresHorarios: [
        "#f43f5e",
        "#6366f1",
        "#0ea5e9",
        "#f97316",
        "#16a34a",
        "#8b5cf6",
        "#facc15",
        "#22d3ee",
        "#ec4899",
        "#14b8a6",
        "#db2777",
        "#0f766e",
      ],
      colorOverrides: [
        {
          pattern: /natalia/i,
          color: "#16a34a",
        },
      ],
      formPersonal: {
        fecha: "",
        hora: "",
        duracion: 60,
        responsable: "",
        titulo: "",
        descripcion: "",
      },

      calendarOptions: {
        plugins: [dayGridPlugin, timeGridPlugin, listPlugin, interactionPlugin],
        initialView: "timeGridWeek",
        headerToolbar: {
          start: "title",
          center: "dayGridMonth,timeGridWeek,timeGridDay,listWeek",
          end: "today prev,next",
        },
        buttonText: {
          today: "Hoy",
          month: "Mes",
          week: "Semana",
          day: "Día",
          list: "Agenda",
        },
        slotMinTime: "07:00:00",
        slotMaxTime: "21:00:00",
        slotDuration: "00:30:00",
        allDaySlot: false,
        nowIndicator: true,
        height: "auto",
        events: [],
        editable: true,
        droppable: true,
        timeZone: "America/Puerto_Rico",
        dateClick: this.abrirModalCitas, // Para abrir el modal con todas las citas del día
        eventClick: this.abrirModalCitaEvento, // Para abrir el modal de una cita específica
        eventDrop: this.actualizarCita,
        eventClassNames: function (arg) {
          const props = arg.event.extendedProps || {};

          if (arg.event.display === "background" || props.tipoCita === "horario") {
            return [];
          }

          if (props.tipoCita === "personal" || props.estado === "personal") {
            return ["evento-personal"];
          }

          if (props.confirmacionCliente) {
            return ["evento-confirmado-cliente"]; // verde
          }

          if (props.estado === "confirmada") {
            return ["evento-confirmado-admin"]; // gris
          }

          if (props.estado === "pendiente") {
            return ["evento-pendiente"];
          }

          return [];
        },
        eventDataTransform: function (event) {
          if (event.display === "background" || event.extendedProps?.tipoCita === "horario") {
            return event;
          }
          return {
            ...event,
            extendedProps: {
              ...event.extendedProps, // conserva todo lo anterior
              tipoCita: event.tipoCita || event.extendedProps?.tipoCita || "",
              estado:
                event.estado || event.extendedProps?.estado || "pendiente",
              asistenciaConfirmada:
                event.asistenciaConfirmada ??
                event.extendedProps?.asistenciaConfirmada ??
                false,
              confirmacionCliente:
                event.confirmacionCliente ??
                event.extendedProps?.confirmacionCliente ??
                false,
            },
          };
        },
      },
    };
  },
  methods: {
    abrirModalCrearCita() {
      this.mostrarModalCrear = true;
    },
    cerrarModalCrear() {
      this.mostrarModalCrear = false;
    },
    abrirModalPersonal() {
      this.resetFormPersonal();
      this.formPersonal.responsable =
        this.selectedEnfermero ||
        Object.keys(this.enfermeros)[0] ||
        "";
      this.mostrarModalPersonal = true;
    },
    cerrarModalPersonal() {
      this.mostrarModalPersonal = false;
    },
    resetFormPersonal() {
      this.formPersonal = {
        fecha: "",
        hora: "",
        duracion: 60,
        responsable: "",
        titulo: "",
        descripcion: "",
      };
    },

    async crearAsuntoPersonal() {
      try {
        const { fecha, hora, duracion, responsable, titulo, descripcion } =
          this.formPersonal;
        if (!fecha || !hora || !duracion || !responsable || !titulo.trim()) {
          alert("Completa fecha, hora, duración, responsable y título.");
          return;
        }

        const dur = Number(duracion) || 0;
        if (!dur) {
          alert("Define una duración válida.");
          return;
        }

        const horaInicio24 = this.convertirHoraAMPMa24(hora);
        if (!horaInicio24) {
          alert("La hora ingresada no es válida.");
          return;
        }
        const horaFin24 = sumarMinutos(horaInicio24, dur);
        const horaAmPm = this.formatearHora24aAMPM(horaInicio24);
        const horaFinAmPm = this.formatearHora24aAMPM(horaFin24);

        const fechaKey = this.normalizarFechaKey(fecha);
        const payload = {
          fecha: fechaKey,
          hora: horaAmPm,
          horaFin: horaFinAmPm,
          duracion: dur,
          title: titulo,
          notaPersonal: descripcion || "",
          enfermero: responsable,
          enfermeroNombre: this.enfermeros[responsable] || "",
          estado: "personal",
          tipoCita: "personal",
          servicios: [],
          balancePendiente: 0,
          confirmacionCliente: true,
          creado: Timestamp.now(),
        };

        await addDoc(collection(db, "citasGlobales"), payload);
        this.cargarCitas();
        this.mostrarModalPersonal = false;
        alert("Asunto personal registrado correctamente.");
      } catch (error) {
        console.error("Error guardando asunto personal:", error);
        alert("No se pudo guardar el asunto personal.");
      }
    },

    async onCitaCreada(citaParcial) {
      try {
        const fechaKey = this.normalizarFechaKey(
          citaParcial.fecha || citaParcial.fechaCita || ""
        );
        const horaInicio24 = citaParcial.hora; // ya en formato "HH:mm"
        const duracion = this.calcularDuracionTotal(citaParcial.servicios);

        const horaFin24 = sumarMinutos(horaInicio24, duracion);
        const horaAmPm = this.formatearHora24aAMPM(horaInicio24);
        const horaFinAmPm = this.formatearHora24aAMPM(horaFin24);

        const balance = citaParcial.servicios.reduce(
          (acc, s) => acc + (s.precio || 0),
          0
        );
        const depositoIngresado = Number(citaParcial.depositoPresencial || 0);
        const depositoConfirmado = Boolean(citaParcial.depositoConfirmado);
        const depositoAplicado = depositoConfirmado ? depositoIngresado : 0;
        const balancePendienteFinal = citaParcial.requierePago
          ? Math.max(balance - depositoAplicado, 0)
          : 0;
        const balancePendienteInicial = citaParcial.requierePago ? balance : 0;

        const payload = {
          ...citaParcial,
          fecha: fechaKey,
          hora: horaAmPm,
          horaFin: horaFinAmPm,
          duracion,
          balancePendiente: balancePendienteInicial,
          pagada: balancePendienteInicial === 0,
          paymentIntentId: null,
          creado: Timestamp.now(),
          estado: "confirmada",
        };

        // Guardar en Firestore
        const ref = await addDoc(
          collection(db, `pacientes/${payload.pacienteId}/citas`),
          payload
        );
        await setDoc(doc(db, "citasGlobales", ref.id), payload);

        let ventaIdDeposito = null;
        if (depositoConfirmado && depositoAplicado > 0) {
          try {
            const productoDeposito = crearProductoDeposito({
              citaId: ref.id,
              paciente: payload.paciente,
              monto: depositoAplicado,
            });
            const { ventaId } = await registrarDepositoEnVentas({
              producto: productoDeposito,
              monto: depositoAplicado,
              metodo: citaParcial.depositoMetodo || "efectivo",
              citaId: ref.id,
              pacienteId: payload.pacienteId,
            });
            ventaIdDeposito = ventaId || null;

            const patchDeposito = {
              balancePendiente: balancePendienteFinal,
              pagada: balancePendienteFinal === 0,
              ventaIdDeposito: ventaId || null,
            };

            await updateDoc(doc(db, "citasGlobales", ref.id), patchDeposito);
            await updateDoc(ref, patchDeposito);
          } catch (depositoError) {
            console.error("❌ Error registrando depósito presencial:", depositoError);
          }
        }

        const servicios = (citaParcial.servicios || []).filter(
          (servicio) => servicio && servicio.nombre
        );
        const balanceTotal = Number(balance) || 0;
        const serviciosTexto = servicios.length
          ? servicios.map((servicio) => servicio.nombre).join(", ")
          : "Por confirmar";
        const fechaFormateada = this.formatDate(fechaKey);
        const horaFormateadaRaw =
          citaParcial.horaAMPM || this.formatearHora24aAMPM(citaParcial.hora || "");
        const horaFormateada =
          horaFormateadaRaw && horaFormateadaRaw !== "Hora inválida"
            ? horaFormateadaRaw
            : "hora no disponible";
        const emailPaciente =
          payload.paciente?.confirmEmail ||
          payload.paciente?.email ||
          payload.email ||
          "";

        if (emailPaciente) {
          try {
            const metodoPagoEtiqueta = {
              efectivo: "Efectivo",
              pos: "POS tarjeta",
              transferencia: "Transferencia",
            }[citaParcial.depositoMetodo] || citaParcial.depositoMetodo || "Efectivo";
            const resumenPago = citaParcial.requierePago
              ? `
                <p style="margin: 0 0 6px 0;"><strong>Valor total:</strong> $${balanceTotal.toFixed(2)}</p>
                <p style="margin: 0 0 6px 0;"><strong>Saldo pendiente:</strong> $${balancePendienteFinal.toFixed(2)}</p>
              `
              : `
                <p style="margin: 0 0 6px 0;"><strong>Valor total:</strong> $${balanceTotal.toFixed(2)}</p>
                <p style="margin: 0 0 6px 0;"><strong>Saldo pendiente:</strong> $0.00</p>
              `;
            const depositoHtml =
              depositoConfirmado && depositoAplicado > 0
                ? `
                  <p style="margin: 0 0 6px 0;"><strong>Depósito cobrado hoy:</strong> $${depositoAplicado.toFixed(
                    2
                  )}</p>
                  <p style="margin: 0 0 6px 0;"><strong>Método de pago:</strong> ${metodoPagoEtiqueta}</p>
                `
                : "";
            const listaServiciosHtml = servicios.length
              ? `
                <ul style="margin: 6px 0 12px 20px; padding: 0;">
                  ${servicios
                    .map(
                      (servicio) =>
                        `<li>${servicio.nombre} · $${Number(
                          servicio.precio || 0
                        ).toFixed(2)}</li>`
                    )
                    .join("")}
                </ul>
              `
              : `<p style="margin: 6px 0 12px 0;"><strong>Tratamientos:</strong> Por confirmar</p>`;
            let pdfBase64 = null;
            let fileName = null;

            if (depositoConfirmado && depositoAplicado > 0) {
              pdfBase64 = await generarPDFCitaDepositoBase64({
                id: ventaIdDeposito ? `VENTA-${ventaIdDeposito}` : `CITA-${ref.id}`,
                fecha: new Date().toISOString(),
                metodoPago: metodoPagoEtiqueta,
                email: emailPaciente,
                servicios,
                valorCita: balanceTotal,
                deposito: depositoAplicado,
                balancePendiente: balancePendienteFinal,
                tienda: {
                  nombre: "NAT Spa Wellness Center",
                  direccion: "Calle Betance #56",
                  telefono: "(787) 415-9799",
                  email: "natspawellnesscenter@yahoo.com",
                },
              });
              fileName = `recibo-cita-${ref.id}.pdf`;
            }

            await enviarCorreoRecibo({
              to: emailPaciente,
              subject: depositoConfirmado
                ? "Confirmación de tu cita y recibo de depósito"
                : "Confirmación de tu cita",
              html: `
                <div style="font-family:system-ui,-apple-system,Segoe UI,Roboto,Helvetica,Arial,sans-serif;color:#0f172a">
                  <h2 style="margin:0 0 8px 0">NAT Spa Wellness Center</h2>
                  <p style="margin:0 0 8px 0">Tu cita ha sido confirmada.</p>
                  <p style="margin:0 0 6px 0"><strong>Fecha:</strong> ${fechaFormateada}</p>
                  <p style="margin:0 0 6px 0"><strong>Hora:</strong> ${horaFormateada}</p>
                  <p style="margin:0 0 6px 0"><strong>Tratamientos:</strong></p>
                  ${listaServiciosHtml}
                  ${depositoHtml}
                  ${resumenPago}
                  <p style="margin:0;color:#64748b;font-size:12px">Calle Betance #56 · (787) 415-9799 · natspawellnesscenter@yahoo.com</p>
                </div>
              `,
              pdfBase64,
              fileName,
            });
            console.log("📧 Correo enviado a:", emailPaciente);
          } catch (emailError) {
            console.error("❌ Error al enviar email de confirmación:", emailError);
          }
        } else {
          console.warn("⚠️ Email del paciente no disponible. No se envía confirmación.");
        }

        // Enviar SMS de confirmación
        try {
          const functions = getFunctions();
          const sendSms = httpsCallable(functions, "sendSms");

          const nombre = payload.paciente?.nombre || "Cliente";
          const apellido = payload.paciente?.apellido || "";
          const nombreCompleto = `${nombre} ${apellido}`.trim();

          let numero = payload.paciente?.telefono?.toString() || "";
          if (!numero.startsWith("+1")) numero = "+1" + numero;

          if (/^\+\d{10,15}$/.test(numero)) {
            let mensaje = `📢 Hola ${nombreCompleto}, tu cita ha sido confirmada para el ${fechaFormateada} a las ${horaFormateada}.\n🧾 Tratamientos: ${serviciosTexto}.\n\n🧖 Nat Spa - Oasis de Paz y Relajación`;

            if (depositoConfirmado && depositoAplicado > 0) {
              mensaje += `\n\n✅ Depósito recibido: $${depositoAplicado.toFixed(
                2
              )}. Saldo pendiente: $${balancePendienteFinal.toFixed(2)}.`;
            } else if (balancePendienteFinal > 0) {
              mensaje += `\n\n💳 Saldo pendiente: $${balancePendienteFinal.toFixed(
                2
              )}. Puedes pagarlo el día de la cita o en la app.`;
            }

            await sendSms({
              to: numero,
              message: mensaje,
            });

            console.log("✅ SMS enviado correctamente:", mensaje);
          } else {
            console.warn("❗ Número de teléfono inválido, no se envió SMS:", numero);
          }
        } catch (smsError) {
          console.error("❌ Error al enviar SMS:", smsError);
        }

        this.cargarCitas();
        this.mostrarModalCrear = false;
        alert("✅ Cita creada correctamente");
      } catch (e) {
        console.error("❌ Error en onCitaCreada:", e);
        alert("Hubo un error al guardar la cita.");
      }
    },


    async obtenerEnfermerosFirestore() {
      const snap = await getDocs(collection(db, "enfermeros"));
      return snap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
    },

    async recalcularDisponibilidad() {
      try {
        const duracion = this.formCita.duracion;
        const fecha = this.formCita.fechaCita;
        const hora = this.formCita.hora;
        const eventoId = this.citaSeleccionada.id;

        const horaInicio = this.convertirHoraAMPMa24(hora);
        const horaFin = sumarMinutos(horaInicio, duracion);

        const horarioBase = await obtenerHorarioBaseDesdeFirestore(
          await this.obtenerEnfermerosFirestore()
        );

        const disponibilidad = obtenerHorariosDisponibles({
          fecha,
          duracion,
          citas: this.citas.filter((c) => c.id !== eventoId),
          horarioBase,
        });

        const slotsNecesarios = generarTimeSlots(
          horaInicio,
          horaFin,
          duracion,
          fecha
        );

        const idsDisponibles = new Set();

        if (slotsNecesarios.length > 0) {
          const candidatos = disponibilidad[slotsNecesarios[0]] || [];
          for (const candidato of candidatos) {
            const cumpleTodos = slotsNecesarios.every((slot) =>
              (disponibilidad[slot] || []).includes(candidato)
            );
            if (cumpleTodos) idsDisponibles.add(candidato);
          }
        }

        // Asegurar que el enfermero actual siga apareciendo
        if (this.citaSeleccionada.enfermeroId) {
          idsDisponibles.add(this.citaSeleccionada.enfermeroId);
        }

        // Armar lista para el select
        const enfermerosFiltrados = {};
        const enfermeros = await this.obtenerEnfermerosFirestore();
        for (const enf of enfermeros) {
          if (idsDisponibles.has(enf.id)) {
            const nombre = `${enf.nombre} ${enf.apellido || ""}`.trim();
            enfermerosFiltrados[enf.id] = nombre;
          }
        }

        this.enfermeros = enfermerosFiltrados;
      } catch (error) {
        console.error("❌ Error al recalcular disponibilidad:", error);
      }
    },
    calcularDuracionTotal(servicios) {
      return (servicios || []).reduce((acc, s) => acc + (s.duracion || 0), 0);
    },

    obtenerColorHorarios(enfermeroId) {
      if (this.colorHorarioDisponible) {
        return this.colorHorarioDisponible;
      }
      const nombre = this.enfermeros[enfermeroId] || "";
      const override = this.colorOverrides.find((entry) =>
        entry.pattern.test(nombre)
      );
      if (override) return override.color;

      if (!enfermeroId) {
        return this.paletaColoresHorarios[0];
      }

      if (!this.coloresHorarios[enfermeroId]) {
        let color = this.paletaColoresHorarios[0];
        if (this.enfermerosLista.length) {
          const index = this.enfermerosLista.findIndex(
            (enf) => enf.id === enfermeroId
          );
          if (index >= 0) {
            color =
              this.paletaColoresHorarios[
                index % this.paletaColoresHorarios.length
              ];
          } else {
            color =
              this.paletaColoresHorarios[
                Object.keys(this.coloresHorarios).length %
                  this.paletaColoresHorarios.length
              ];
          }
        } else {
          const keys = Object.keys(this.coloresHorarios);
          color =
            this.paletaColoresHorarios[
              keys.length % this.paletaColoresHorarios.length
            ];
        }

        this.coloresHorarios = {
          ...this.coloresHorarios,
          [enfermeroId]: color,
        };
      }

      return this.coloresHorarios[enfermeroId];
    },

    obtenerNombreEnfermero(enfermeroId) {
      return this.enfermeros[enfermeroId] || "";
    },

    generarEventosHorarios(bloques = []) {
      const rangos = this.normalizarBloquesHorarios(bloques || []);
      const eventos = rangos
        .map((rango) => {
          const enfermeroId = rango.enfermeroId || "";
          const color = this.obtenerColorHorarios(enfermeroId);
          const start = this.combinarFechaHoraPr(
            rango.fechaKey,
            this.minutosAHora(rango.inicioMin)
          );
          const end = this.combinarFechaHoraPr(
            rango.fechaKey,
            this.minutosAHora(rango.finMin)
          );
          if (!start || !end) return null;
          return {
            id: `${enfermeroId}-${rango.fechaKey}-${rango.inicioMin}-${rango.finMin}`,
            title: this.obtenerNombreEnfermero(enfermeroId) || "Horario",
            start,
            end,
            display: "background",
            backgroundColor: color,
            borderColor: color,
            classNames: ["horario-esteticista"],
            overlap: true,
            editable: false,
            extendedProps: {
              enfermeroId,
              tipoCita: "horario",
            },
          };
        })
        .filter(Boolean);
      const intervalos = this.generarIntervalosHorarios(rangos);
      this.horariosEventos = eventos;
      this.intervalosEventos = intervalos;
      this.actualizarEventosCalendario();
    },

    generarIntervalosHorarios(rangos = []) {
      const grupos = {};
      (rangos || []).forEach((rango) => {
        const key = `${rango.enfermeroId}|${rango.fechaKey}`;
        if (!grupos[key]) grupos[key] = [];
        grupos[key].push(rango);
      });

      const eventos = [];
      Object.values(grupos).forEach((lista) => {
        const ordenados = lista.sort((a, b) => a.inicioMin - b.inicioMin);
        for (let i = 0; i < ordenados.length - 1; i += 1) {
          const actual = ordenados[i];
          const siguiente = ordenados[i + 1];
          if (!actual || !siguiente) continue;
          if (siguiente.inicioMin <= actual.finMin) continue;

          const start = this.combinarFechaHoraPr(
            actual.fechaKey,
            this.minutosAHora(actual.finMin)
          );
          const end = this.combinarFechaHoraPr(
            actual.fechaKey,
            this.minutosAHora(siguiente.inicioMin)
          );
          if (!start || !end) continue;

          eventos.push({
            id: `${actual.enfermeroId}-${actual.fechaKey}-intervalo-${actual.finMin}-${siguiente.inicioMin}`,
            title: "Intervalo",
            start,
            end,
            display: "background",
            backgroundColor: "#0f172a",
            borderColor: "#0f172a",
            classNames: ["intervalo-horario"],
            overlap: true,
            editable: false,
            extendedProps: {
              enfermeroId: actual.enfermeroId,
              tipoCita: "intervalo",
            },
          });
        }
      });

      return eventos;
    },

    horaStrAMinutos(hora24) {
      if (!hora24 || typeof hora24 !== "string") return null;
      const partes = hora24.split(":");
      if (partes.length < 2) return null;
      const horas = Number(partes[0]);
      const minutos = Number(partes[1]);
      if (!Number.isFinite(horas) || !Number.isFinite(minutos)) return null;
      return horas * 60 + minutos;
    },

    minutosAHora(minutos) {
      if (!Number.isFinite(minutos)) return "";
      const horas = Math.floor(minutos / 60);
      const mins = minutos % 60;
      return `${String(horas).padStart(2, "0")}:${String(mins).padStart(2, "0")}`;
    },

    normalizarBloquesHorarios(bloques = []) {
      const mapa = new Map();
      (bloques || []).forEach((bloque) => {
        if (!bloque?.start || !bloque?.end) return;
        const enfermeroId = bloque.enfermeroId || "";
        const fechaKey =
          bloque.fechaKey || this.extraerFechaKeyPr(bloque.start);
        if (!fechaKey) return;

        const inicioStr = formatIsoPrTime(bloque.start);
        const finStr = formatIsoPrTime(bloque.end);
        const inicioMin = this.horaStrAMinutos(inicioStr);
        const finMin = this.horaStrAMinutos(finStr);
        if (
          !Number.isFinite(inicioMin) ||
          !Number.isFinite(finMin) ||
          inicioMin >= finMin
        ) {
          return;
        }

        const key = `${enfermeroId}|${fechaKey}|${inicioMin}|${finMin}`;
        if (!mapa.has(key)) {
          mapa.set(key, { inicioMin, finMin, enfermeroId, fechaKey });
        }
      });

      return Array.from(mapa.values()).sort((a, b) => {
        if (a.fechaKey !== b.fechaKey) {
          return a.fechaKey.localeCompare(b.fechaKey);
        }
        if (a.enfermeroId !== b.enfermeroId) {
          return a.enfermeroId.localeCompare(b.enfermeroId);
        }
        return a.inicioMin - b.inicioMin;
      });
    },

    obtenerHorariosFiltrados() {
      if (!this.selectedEnfermero) return this.horariosEventos || [];
      return (this.horariosEventos || []).filter(
        (evento) =>
          evento.extendedProps?.enfermeroId === this.selectedEnfermero
      );
    },

    obtenerIntervalosFiltrados() {
      if (!this.selectedEnfermero) return [];
      return (this.intervalosEventos || []).filter(
        (evento) =>
          evento.extendedProps?.enfermeroId === this.selectedEnfermero
      );
    },

    actualizarEventosCalendario() {
      const horarios = this.obtenerHorariosFiltrados();
      const intervalos = this.obtenerIntervalosFiltrados();
      this.calendarOptions.events = [
        ...horarios,
        ...intervalos,
        ...this.citasFiltradas,
      ];
    },

    esCitaPersonal(cita) {
      const tipo = cita?.tipoCita || cita?.estado || "";
      return String(tipo).toLowerCase() === "personal";
    },

    normalizarHoraParaPicker(hora) {
      if (!hora || typeof hora !== "string") return "";
      const raw = hora.trim();
      if (!raw) return "";
      const hora24 = this.convertirHoraAMPMa24(raw);
      if (hora24) return this.formatearHora24aAMPM(hora24);
      return raw;
    },

    convertirHoraAMPMa24(horaInput) {
      if (!horaInput) return "";
      const rawInitial = String(horaInput).trim();
      if (!rawInitial) return "";

      // Si viene un ISO con fecha, usar la conversión a hora PR primero
      if (rawInitial.includes("T")) {
        const prTime = formatIsoPrTime(rawInitial);
        if (prTime) return prTime;
      }

      // Normalizar formatos como "2:30pm", "2:30 p.m." o "2:30 p. m."
      let raw = rawInitial
        .replace(/\s+/g, " ")
        .replace(/a\.?\s*m\.?/gi, "AM")
        .replace(/p\.?\s*m\.?/gi, "PM")
        .replace(/\s*(AM|PM)$/i, " $1")
        .trim();
      if (!raw) return "";

      const matchAmPm = raw.match(
        /^(\d{1,2}:\d{2})(?::\d{2})?\s*(AM|PM)$/i
      );
      if (!matchAmPm) {
        const match24 = raw.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
        if (match24) {
          const horas = match24[1].padStart(2, "0");
          const minutos = match24[2].padStart(2, "0");
          return `${horas}:${minutos}`;
        }
        return "";
      }

      const time = matchAmPm[1];
      const modifier = matchAmPm[2].toUpperCase();
      let [hours, minutes] = time.split(":").map(Number);
      if (modifier === "PM" && hours !== 12) hours += 12;
      if (modifier === "AM" && hours === 12) hours = 0;
      return `${hours.toString().padStart(2, "0")}:${minutes
        .toString()
        .padStart(2, "0")}`;
    },

    formatearHora24aAMPM(hora24) {
      if (!hora24 || typeof hora24 !== "string" || !hora24.includes(":"))
        return "Hora inválida";
      const [hourStr, minute] = hora24.split(":");
      let hour = parseInt(hourStr);
      const ampm = hour >= 12 ? "PM" : "AM";
      hour = hour % 12 || 12;
      return `${hour.toString().padStart(2, "0")}:${minute} ${ampm}`;
    },

    horaFinCalculada() {
      try {
        const horaAMPM = this.formCita.hora;
        const duracion = parseInt(this.formCita.duracion);
        if (!horaAMPM || isNaN(duracion)) return "";

        const hora24 = this.convertirHoraAMPMa24(horaAMPM);
        const nuevaHora24 = sumarMinutos(hora24, duracion);

        return this.formatearHora24aAMPM(nuevaHora24); // aquí está el fix
      } catch (err) {
        console.error(" Error en horaFinCalculada:", err);
        return "";
      }
    },
    actualizarHoraFin() {
    const hora = this.formCita.hora;
    const duracion = parseInt(this.formCita.duracion);

    if (!hora || isNaN(duracion)) {
      this.citaSeleccionada.horaFin = "";
      return;
    }

    const hora24 = this.convertirHoraAMPMa24(hora);
    const nuevaHora = sumarMinutos(hora24, duracion);
    this.citaSeleccionada.horaFin = this.formatearHora24aAMPM(nuevaHora);
  },


    formatDate(csharpDate) {
      if (!csharpDate) return "";

      try {
        // Split the input string at 'T' to separate date and time
        const datePart = csharpDate.split("T")[0]; // Get "2024-12-25"

        // Split the date into day, month, and year
        const [year, month, day] = datePart.split("-");

        // Return the formatted date as dd-MM-yyyy
        return `${day}-${month}-${year}`;
      } catch (error) {
        console.error("Invalid date format:", error);
        return "Invalid Date";
      }
    },

    async cambiarModo(modo = "edicion", cita = null) {
      if (modo === "edicion") {
        const citaAEditar = cita || this.citasDelDia[0];

        if (!citaAEditar) {
          alert("No hay cita válida para editar.");
          return;
        }

        this.modalModo = "edicion";
        this.citaExpandidaId = citaAEditar.id || null;

        // Copia profunda real sin referencias compartidas
        const copia = structuredClone
          ? structuredClone(citaAEditar)
          : JSON.parse(JSON.stringify(citaAEditar));
        this.citaSeleccionada = copia;

        // Copia aislada del form para evitar mutaciones
        this.formCita = {
          hora: this.normalizarHoraParaPicker(copia.hora || ""),
          fechaCita:
            copia.fecha ||
            (copia.start instanceof Date
              ? copia.start.toISOString().split("T")[0]
              : this.formatDate2(new Date(copia.start), false)),
          enfermero: copia.enfermeroId || "",
          duracion:
            copia.duracion || this.calcularDuracionTotal(copia.servicios),
          servicios: copia.servicios
            ? JSON.parse(JSON.stringify(copia.servicios))
            : [],
          titulo: this.esCitaPersonal(copia) ? copia.title || "" : "",
          notaPersonal: copia.notaPersonal || "",
        };

        try {
  const fecha = this.formCita.fechaCita;
  const hora = this.formCita.hora;
  const duracion = this.formCita.duracion;

  if (duracion && fecha && hora) {
    const enfermerosSnap = await getDocs(collection(db, "enfermeros"));
    const enfermerosArray = enfermerosSnap.docs.map((d) => ({
      id: d.id,
      ...d.data(),
    }));
    const horarioBase = await obtenerHorarioBaseDesdeFirestore(enfermerosArray);

    const disponibilidad = obtenerHorariosDisponibles({
      fecha,
      duracion,
      citas: this.citas.filter(
        (c) => c.id !== this.citaSeleccionada.id
      ),
      horarioBase,
    });

    const horaInicio = this.convertirHoraAMPMa24(hora);
    const horaFin = sumarMinutos(horaInicio, duracion);
    const slotsNecesarios = generarTimeSlots(
      horaInicio,
      horaFin,
      duracion,
      fecha,
      true
    );

    const idsDisponibles = new Set();

if (slotsNecesarios.length > 0) {
  const disponiblesPorSlot = slotsNecesarios.map(
    (slot) => new Set(disponibilidad[slot] || [])
  );

  if (disponiblesPorSlot.length > 0) {
    const interseccion = [...disponiblesPorSlot[0]].filter((uid) =>
      disponiblesPorSlot.every((set) => set.has(uid))
    );

    interseccion.forEach((uid) => idsDisponibles.add(uid));
  }
}

// 🔥 Ya no forzamos agregar al enfermero actual
// ⛔ if (this.formCita.enfermero) idsDisponibles.add(this.formCita.enfermero);

const enfermerosFiltrados = {};
for (const doc of enfermerosSnap.docs) {
  const uid = doc.id;
  if (idsDisponibles.has(uid)) {
    const nombre = `${doc.data().nombre} ${doc.data().apellido || ""}`.trim();
    enfermerosFiltrados[uid] = nombre;
  }
}

this.enfermeros = enfermerosFiltrados;


    this.enfermeros = enfermerosFiltrados;

    console.log("📋 Fecha:", fecha);
    console.log("🕓 Hora:", hora);
    console.log("⏱️ Duración:", duracion);
    console.log("⏳ Hora inicio (24h):", horaInicio);
    console.log("⏱️ Hora fin:", horaFin);
    console.log("🔢 Slots necesarios:", slotsNecesarios);
    console.log("🧠 Disponibilidad por slot:", disponibilidad);
    console.log("👥 Enfermeros disponibles (IDs):", [...idsDisponibles]);
    console.table(this.enfermeros);
  }
} catch (error) {
  console.error("❌ Error al calcular disponibilidad de esteticistas:", error);
}

      } else {
        this.modalModo = "vista";
        this.formCita = {
          hora: "",
          enfermero: "",
          fechaCita: "",
          servicios: [],
          duracion: 0,
          titulo: "",
          notaPersonal: "",
        };
        this.citaSeleccionada = null;
        if (this.citasDelDia.length) {
          this.citaExpandidaId = this.citasDelDia[0]?.id || null;
        }
      }
    },

    async confirmarManualCliente(cita) {
      try {
        const citaRef = doc(db, "citasGlobales", cita.id);
        await updateDoc(citaRef, {
          asistenciaConfirmada: true,
          confirmacionCliente: true,
        });
        alert(" Confirmación manual registrada.");
        this.cargarCitas();
      } catch (e) {
        console.error(" Error al confirmar manual:", e);
      }
    },

    async enviarRecordatorioCliente(cita) {
      try {
        const sendSms = httpsCallable(getFunctions(), "sendSms");

        const fecha = this.formatDate(cita.start); //  Formato dd-mm-yyyy
        const hora = cita.hora || "hora no disponible";
        const nombre = cita.title || "Paciente";

        const mensaje = ` Hola ${nombre}, recuerda confirmar tu cita para el ${fecha} a las ${hora}.\n\n Nat Spa - Oasis de Paz y Relajación`;

        let numero = cita.telefono?.toString() || "";
        if (!numero.startsWith("+1")) numero = "+1" + numero;

        await sendSms({
          to: numero,
          message: mensaje,
        });

        console.log(" Recordatorio enviado:", mensaje);
        alert(" Recordatorio enviado con éxito.");
      } catch (error) {
        console.error(" Error al enviar recordatorio:", error);
        alert("Ocurrió un error al enviar el mensaje.");
      }
    },

    async aprobarCita(cita) {
      try {
        const citaId = cita?.id;

        if (!citaId) {
          alert(" No hay cita válida seleccionada.");
          return;
        }

        const citaRef = doc(db, "citasGlobales", citaId);
        const citaSnap = await getDoc(citaRef);

        if (!citaSnap.exists()) {
          alert(" La cita no existe en la base de datos.");
          return;
        }

        this.citaSeleccionada = {
          id: citaRef.id,
          ...citaSnap.data(),
        };
        this.formCita = {
          ...this.formCita,
          fechaCita: cita.fecha || cita.start?.split("T")[0] || "",
          hora: this.normalizarHoraParaPicker(cita.hora || ""),
          enfermero: cita.enfermeroId || this.formCita.enfermero || null,
        };

        if (
          !this.formCita.fechaCita ||
          !this.formCita.hora ||
          !this.formCita.enfermero
        ) {
          alert(" Faltan datos necesarios para confirmar la cita.");
          return;
        }

        await updateDoc(citaRef, {
          enfermero: this.formCita.enfermero,
          fecha: this.formCita.fechaCita,
          hora: this.normalizarHoraParaPicker(this.formCita.hora) || this.formCita.hora,
          estado: "confirmada",
          tratamientoCompletado: false,
        });

        console.log(" Cita actualizada");

        const functions = getFunctions();

        if (cita.paymentIntentId) {
          const confirmarPago = httpsCallable(functions, "confirmarPagoCita");
          await confirmarPago({
            intentId: cita.paymentIntentId,
            citaId: cita.id,
          });
          console.log(" Pago confirmado");

          // Calcular el 50% de la cita
          const totalReal = cita.servicios.reduce(
            (sum, s) => sum + parseFloat(s.precio || 0),
            0
          );
          const totalPagado = totalReal * 0.5; // El 50% de la cita
          const balancePendiente = totalReal - totalPagado; // Lo que queda por pagar

          /// Registrar la venta en Firestore SOLO POR LO CAPTURADO
          await addDoc(collection(db, "ventas"), {
            fecha: Timestamp.now(),
            metodoPago: "stripe",
            subtotal: totalPagado,
            impuestos: 0,
            total: totalPagado,
            valorTotalServicio: totalReal,
            origen: "servicio-presencial",
            descripcion: "Servicios confirmados",
            items: (cita.servicios || []).map((s) => ({
              nombre: s?.nombre || "Servicio sin nombre",
              precio: parseFloat(s?.precio || 0),
              cantidad: 1,
              tipo: "servicio",
            })),

            pacienteId: cita.pacienteId || null,
            citaId: cita.id || null,
          });
          console.log(" Venta registrada correctamente por lo cobrado.");

          //  Generar el PDF NUEVO con logo y resumen depósito 50% (sin callbacks)
          const base64 = await generarPDFCitaDepositoBase64({
            id: `CITA-${cita.id || "unknown"}`,
            fecha: new Date().toISOString(),
            metodoPago: "stripe",
            email:
              cita.paciente?.confirmEmail ||
              cita.paciente?.email ||
              cita.email ||
              "",

            productos: cita.servicios
              .filter((s) => s && s.nombre)
              .map((s) => ({
                nombre: s.nombre || "Servicio sin nombre",
                cantidad: 1,
                precio: parseFloat(s.precio || 0),
                impuesto: 0,
              })),

            valorCita: totalReal,
            deposito: totalPagado,
            balancePendiente: balancePendiente,

            subtotal: totalPagado,
            impuestos: 0,
            total: totalPagado,
            tienda: {
              nombre: "NAT Spa Wellness Center",
              direccion: "Calle Betance #56",
              telefono: "(787) 415-9799",
              email: "natspawellnesscenter@yahoo.com",
            },
          });

          //  Enviar email con el NUEVO PDF
          const emailPaciente =
            cita.paciente?.confirmEmail ||
            cita.paciente?.email ||
            cita.email ||
            "";
          if (emailPaciente) {
            await enviarCorreoRecibo({
              to: emailPaciente,
              subject: "Confirmación de tu cita y recibo (depósito 50%)",
              html: `
      <div style="font-family:system-ui,-apple-system,Segoe UI,Roboto,Helvetica,Arial,sans-serif;color:#0f172a">
        <h2 style="margin:0 0 8px 0">NAT Spa Wellness Center</h2>
        <p style="margin:0 0 8px 0">Hemos recibido tu <strong>depósito del 50%</strong> para confirmar la cita.</p>
        <p style="margin:0 0 4px 0"><strong>Valor de la cita:</strong> $${totalReal.toFixed(
          2
        )}</p>
        <p style="margin:0 0 4px 0"><strong>Depósito cobrado hoy:</strong> $${totalPagado.toFixed(
          2
        )}</p>
        <p style="margin:0 0 12px 0"><strong>Saldo pendiente:</strong> $${balancePendiente.toFixed(
          2
        )} (se paga el día de la cita)</p>
        <p style="margin:0;color:#64748b;font-size:12px">Calle Betance #56 · (787) 415-9799 · natspawellnesscenter@yahoo.com</p>
      </div>
    `,
              pdfBase64: base64,
              fileName: `recibo-cita-${cita.id}.pdf`,
            });
            console.log("Correo enviado a:", emailPaciente);
          } else {
            console.warn(
              " Email del paciente no disponible. No se envía el recibo."
            );
          }

          // Enviar SMS
          const sendSms = httpsCallable(functions, "sendSms");
          let numero = cita.telefono?.toString() || "";
          if (!numero.startsWith("+1")) numero = "+1" + numero;

          const fechaFormateada = this.formatDate(
            cita.fecha || this.formCita.fechaCita
          );

          await sendSms({
            to: numero,
            message: `Su cita ha sido confirmada para el ${fechaFormateada} a las ${this.formCita.hora}.\n\nNat Spa\nOasis De Paz y Relajacion`,
          });

          console.log(" SMS enviado a:", numero);

          alert(" Cita confirmada, pago procesado y mensaje enviado.");
          this.mostrarModal = false;
          this.cargarCitas();
        }
      } catch (error) {
        console.error(" Error al aprobar cita:", error);
        alert("Ocurrió un error al confirmar la cita.");
      }
    },

    async guardarCambios() {
      try {
        const cita = this.citaSeleccionada;
        if (!cita || !cita.id) {
          alert("No hay cita seleccionada para editar.");
          return;
        }

        const esPersonal = this.esCitaPersonal(cita);
        const citaRef = doc(db, "citasGlobales", cita.id);
        const citaDoc = await getDoc(citaRef);
        if (!citaDoc.exists()) {
          alert("La cita no existe en Firestore.");
          return;
        }

        if (
          !this.formCita.hora ||
          !this.formCita.enfermero ||
          !this.formCita.fechaCita
        ) {
          alert("Por favor, completa todos los campos obligatorios.");
          return;
        }
        if (esPersonal && !this.formCita.titulo?.trim()) {
          alert("El asunto personal requiere un título.");
          return;
        }

        const horaInicio24 = this.convertirHoraAMPMa24(this.formCita.hora);
        if (!horaInicio24) {
          alert("La hora ingresada no es válida.");
          return;
        }
        const duracionManual = parseInt(this.formCita.duracion) || 60;
        const horaFin24 = sumarMinutos(horaInicio24, duracionManual);
        const horaAmPm = this.formatearHora24aAMPM(horaInicio24);
        const horaFinAmPm = this.formatearHora24aAMPM(horaFin24);

        // resolver nombre del enfermero para persistir
        const nombreEnfermero =
          this.enfermeros[this.formCita.enfermero] ||
          this.formCita.enfermero ||
          "";

        const updatePayload = {
          fecha: this.formCita.fechaCita,
          hora: horaAmPm,
          enfermero: this.formCita.enfermero,
          enfermeroNombre: nombreEnfermero,
          duracion: duracionManual,
          horaFin: horaFinAmPm,
        };

        if (esPersonal) {
          updatePayload.title = this.formCita.titulo.trim();
          updatePayload.notaPersonal = this.formCita.notaPersonal || "";
          updatePayload.tipoCita = "personal";
          updatePayload.estado = "personal";
        }

        await updateDoc(citaRef, updatePayload);

        const citaActualizada = {
          ...cita,
          hora: horaAmPm,
          fecha: this.formCita.fechaCita,
          horaFin: horaFinAmPm,
          enfermeroId: this.formCita.enfermero,
          enfermero: this.enfermeros[this.formCita.enfermero] || "Sin asignar",
          start: this.combinarFechaHoraPr(
            this.formCita.fechaCita,
            horaInicio24
          ),
          end: this.combinarFechaHoraPr(
            this.formCita.fechaCita,
            horaFin24
          ),
          tipoCita: esPersonal ? "personal" : cita.tipoCita || "",
          estado: esPersonal ? "personal" : cita.estado || "pendiente",
          title: esPersonal ? this.formCita.titulo.trim() : cita.title,
          notaPersonal: esPersonal ? this.formCita.notaPersonal || "" : "",
        };

        // 🔁 Actualizar en todas las listas
        const updateLista = (lista) => {
          const idx = lista.findIndex((c) => c.id === cita.id);
          if (idx !== -1) lista[idx] = { ...lista[idx], ...citaActualizada };
        };

        updateLista(this.citas);
        updateLista(this.citasFiltradas);
        updateLista(this.citasDelDia);

        this.actualizarEventosCalendario();

        // Comparar fecha y hora anterior con las nuevas
        const citaDocData = citaDoc.data();
        const fechaAnterior = citaDocData.fecha;
        const horaAnterior =
          this.convertirHoraAMPMa24(citaDocData.hora) || "";

        const nuevaFecha = this.formCita.fechaCita;
        const nuevaHora = horaInicio24;

        // Verificar si hubo cambio real
        const fechaCambio = fechaAnterior !== nuevaFecha;
        const horaCambio = horaAnterior !== nuevaHora;

        //  Enviar SMS solo si cambió fecha u hora y no es asunto personal
        if (!esPersonal) {
          try {
            if (fechaCambio || horaCambio) {
              const functions = getFunctions();
              const sendSms = httpsCallable(functions, "sendSms");

              const paciente = citaDocData.paciente || {};
              const nombre = paciente.nombre || "Cliente";
              const apellido = paciente.apellido || "";
              const nombreCompleto = `${nombre} ${apellido}`.trim();

              const fechaFormateada = this.formatDate(this.formCita.fechaCita);
              const hora = horaAmPm || this.formCita.hora || "hora no disponible";

              let numero = paciente.telefono?.toString() || "";
              if (!numero.startsWith("+1")) numero = "+1" + numero;

              if (!/^\+\d{10,15}$/.test(numero)) {
                alert("El número de teléfono del cliente no es válido.");
                return;
              }

              const mensaje = `📢 Hola ${nombreCompleto}, tu cita ha sido actualizada para el ${fechaFormateada} a las ${hora}.\n\n🧖 Nat Spa - Oasis de Paz y Relajación`;

              await sendSms({
                to: numero,
                message: mensaje,
              });

              console.log("SMS enviado correctamente:", mensaje);
            } else {
              console.log("📭 No se envió SMS: la fecha y hora no cambiaron.");
            }
          } catch (smsError) {
            console.error("Error al enviar SMS:", smsError);
            alert("Hubo un problema al enviar el SMS de confirmación.");
          }
        }

        alert(" Cita actualizada correctamente");
        this.cambiarModo("vista");
      } catch (error) {
        console.error(" Error al actualizar la cita:", error);
        alert("Ocurrió un error al guardar los cambios.");
      }
    },

    async cargarEnfermerosSinFiltro(extraUid = null) {
      try {
        const querySnapshot = await getDocs(collection(db, "enfermeros"));
        const enfermeros = {};

        for (const doc of querySnapshot.docs) {
          const uid = doc.id;
          const data = doc.data();
          const nombre = `${data.nombre} ${data.apellido || ""}`.trim();

          const disponibilidadSnap = await getDocs(
            collection(db, "horarios", uid, "disponibilidad")
          );

          if (!disponibilidadSnap.empty || uid === extraUid) {
            enfermeros[uid] = nombre;
          }
        }

        this.enfermeros = enfermeros;
      } catch (error) {
        console.error(" Error al cargar enfermeros sin filtro:", error);
        this.enfermeros = {};
      }
    },

    async cargarEnfermeros() {
      try {
        const querySnapshot = await getDocs(collection(db, "enfermeros"));
        const enfermerosArray = querySnapshot.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        }));
        const enfermeros = {};
        enfermerosArray.forEach((enf) => {
          const nombre = `${enf.nombre || ""} ${enf.apellido || ""}`.trim();
          if (nombre) {
            enfermeros[enf.id] = nombre;
          }
        });
        this.enfermeros = enfermeros;
        this.enfermerosLista = enfermerosArray;
        this.coloresHorarios = {};
        return enfermerosArray;
      } catch (error) {
        console.error("Error al cargar enfermeros con disponibilidad:", error);
        return [];
      }
    },

    async cargarCitas() {
      try {
        const enfermerosArray =
          (await this.cargarEnfermeros()) || [];
        const horarioBase = await obtenerHorarioBaseDesdeFirestore(
          enfermerosArray
        );
        const bloques = horarioBase.bloques || [];
        if (bloques.length) {
          const idsConBloques = new Set(
            bloques.map((bloque) => bloque.enfermeroId)
          );
          const enfermerosDisponibles = {};
          enfermerosArray.forEach((enf) => {
            if (idsConBloques.has(enf.id)) {
              const nombre = `${enf.nombre || ""} ${enf.apellido || ""}`.trim();
              if (nombre) {
                enfermerosDisponibles[enf.id] = nombre;
              }
            }
          });
          if (Object.keys(enfermerosDisponibles).length) {
            this.enfermeros = enfermerosDisponibles;
          }
        }
        this.generarEventosHorarios(bloques);

        const querySnapshot = await getDocs(collection(db, "citasGlobales"));
        
        const citas = querySnapshot.docs.map((doc) => {
          const data = doc.data();
          const paciente = data.paciente || {};
          const esPersonal =
            data.tipoCita === "personal" ||
            data.estado === "personal" ||
            Boolean(data.notaPersonal);
          const tituloPersonal =
            (typeof data.title === "string" && data.title.trim()) ||
            (typeof data.titulo === "string" && data.titulo.trim()) ||
            (typeof data.motivo === "string" && data.motivo.trim()) ||
            (typeof data.notaPersonal === "string" &&
              data.notaPersonal.trim()) ||
            "Asunto personal";
          const nombrePaciente = `${paciente.nombre || ""} ${
            paciente.apellido || ""
          }`.trim();
          const titulo = esPersonal ? tituloPersonal : nombrePaciente || "Nombre";
          const fechaKey = this.normalizarFechaKey(
            data.fecha || data.fechaCita || ""
          );
          const horaRaw = data.hora || "";
          const horaDisplay = this.normalizarHoraParaPicker(horaRaw) || horaRaw;
          const horaInicio = this.formatTime(horaRaw);
          const horaFin = this.formatTime(data.horaFin || "");
          const horaFinSeguro =
            horaFin && horaFin !== "Invalid Time" ? horaFin : horaInicio;
          const inicioIso = this.combinarFechaHoraPr(fechaKey, horaInicio);
          const finIso = this.combinarFechaHoraPr(fechaKey, horaFinSeguro);
          const duracionTotal =
            Number(data.duracion) || this.calcularDuracionTotal(data.servicios);
          const horaFinDisplay =
            this.normalizarHoraParaPicker(data.horaFin || "") ||
            (data.horaFin && /AM|PM/i.test(data.horaFin)
              ? data.horaFin
              : this.formatearHora24aAMPM(data.horaFin));

          // Resolver nombre del enfermero con fallbacks
          let enfermeroNombre = "";
          if (data.enfermeroNombre && String(data.enfermeroNombre).trim()) {
            enfermeroNombre = String(data.enfermeroNombre).trim();
          } else if (data.enfermero && this.enfermeros[data.enfermero]) {
            enfermeroNombre = this.enfermeros[data.enfermero];
          } else if (data.enfermero && typeof data.enfermero === "string") {
            enfermeroNombre = data.enfermero; // posible nombre literal
          }

          return {
            id: doc.id,
            title: titulo,
            start: inicioIso || `${fechaKey}T${horaInicio}`,
            end: finIso || `${fechaKey}T${horaFinSeguro}`,
            hora: horaDisplay,
            horaFin: horaFinDisplay,
            telefono: paciente.telefono || "No disponible",
            email: paciente.email || "No disponible",
            enfermero:
              enfermeroNombre ||
              this.enfermeros[data.enfermero] ||
              "Sin asignar",
            enfermeroNombre: enfermeroNombre || "",
            enfermeroId: data.enfermero || null,
            paymentIntentId: data.paymentIntentId || null,
            prioridad: data.prioridad || "normal",
            estado: esPersonal ? "personal" : data.estado || "pendiente",
            tipoCita: esPersonal ? "personal" : data.tipoCita || "",
            asistenciaConfirmada: data.asistenciaConfirmada || false,
            confirmacionCliente: data.confirmacionCliente || false,
            servicios: data.servicios || [],
            duracion: duracionTotal,
            balancePendiente: data.balancePendiente || 0,
            notaPersonal: data.notaPersonal || data.descripcion || "",
          };
        });

        this.citas = citas;
        this.citasFiltradas = this.citas.filter(
          (c) =>
            this.esCitaPersonal(c) ||
            ["pendiente", "confirmada"].includes(c.estado) ||
            c.asistenciaConfirmada
        );
        this.actualizarEventosCalendario();
      } catch (error) {
        console.error("Error al cargar las citas:", error);
      }
    },

    filtrarCitas() {
      const base = this.citas.filter(
        (c) =>
          this.esCitaPersonal(c) ||
          ["pendiente", "confirmada"].includes(c.estado) ||
          c.asistenciaConfirmada
      );

      this.citasFiltradas = this.selectedEnfermero
        ? base.filter((c) => c.enfermeroId === this.selectedEnfermero)
        : base;

      this.actualizarEventosCalendario();
    },


    abrirModalCitas(info) {
      console.log(" abrirModalCitas llamado con info:", info);

      const fechaISO = formatIsoPrDateKey(info?.dateStr || info?.date);

      console.log(" fechaISO calculada:", fechaISO);

      if (fechaISO) {
        const fechaBase = new Date(`${fechaISO}T12:00:00Z`);
        this.modalFecha = fechaBase.toLocaleDateString("es-PR", {
          timeZone: "America/Puerto_Rico",
          weekday: "long",
          year: "numeric",
          month: "long",
          day: "numeric",
        });
      } else {
        this.modalFecha = "";
      }

      this.citasDelDia = this.citasFiltradas.filter((cita, i) => {
        try {
          if (!cita.start && !cita.fecha) {
            console.warn(` Cita ${i} sin campo start:`, cita);
            return false;
          }

          const citaISO = formatIsoPrDateKey(cita.start || cita.fecha);
          const match = citaISO === fechaISO;

          console.log(" Problema de fechas:", {
            id: cita.id,
            title: cita.title,
            citaISO,
            fechaISO,
            match,
          });

          return match;
        } catch (err) {
          console.error(`Error procesando cita ${i}:`, err, cita);
          return false;
        }
      });

      console.log(
        " citasDelDia encontradas:",
        this.citasDelDia.length,
        this.citasDelDia
      );
      const existeSeleccion = this.citasDelDia.some(
        (cita) => cita.id === this.citaExpandidaId
      );
      if (!existeSeleccion) {
        this.citaExpandidaId = this.citasDelDia[0]?.id || null;
      }
      this.modalModo = "vista";
      this.mostrarModal = true;
    },
    async abrirModalCitaEvento(info) {
      try {
        const evento = info.event;
        this.citaExpandidaId = evento.id;
        const startDate = new Date(evento.start);
        if (isNaN(startDate)) throw new Error("Fecha inválida");

        const fecha = this.formatDate2(startDate, false);
        const hora = this.formatDate2(startDate, true);
        const citaRef = doc(db, "citasGlobales", evento.id);
        const citaSnap = await getDoc(citaRef);

        if (!citaSnap.exists()) {
          alert(" No se encontró la cita.");
          return;
        }

        const data = citaSnap.data();
        const enfermeroId = data.enfermero || null;
        const servicios = data.servicios || [];

        const duracionTotal =
          Number(data.duracion) || this.calcularDuracionTotal(servicios) || 0;

        const enfermerosSnap = await getDocs(collection(db, "enfermeros"));
        const enfermerosArray = enfermerosSnap.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        }));
        const horarioBase = await obtenerHorarioBaseDesdeFirestore(
          enfermerosArray
        );

        const disponibilidad = obtenerHorariosDisponibles({
  fecha,
  duracion: duracionTotal,
  citas: this.citas.filter((c) => c.id !== evento.id),
  horarioBase,
});

const horaInicio = this.convertirHoraAMPMa24(data.hora || hora);
const horaFinCalculada = sumarMinutos(horaInicio, duracionTotal);

// 3. Generar los slots necesarios para el rango total de la cita
const slotsNecesarios = generarTimeSlots(
  horaInicio,
  horaFinCalculada,
  duracionTotal,
  fecha
);

// 4. Obtener enfermeros disponibles en ALGÚN slot
 const idsDisponibles = new Set();

if (slotsNecesarios.length > 0) {
  const contador = {};

  for (const slot of slotsNecesarios) {
    const candidatos = disponibilidad[slot] || [];
    for (const candidato of candidatos) {
      contador[candidato] = (contador[candidato] || 0) + 1;
    }
  }

  for (const [uid, count] of Object.entries(contador)) {
    if (count === slotsNecesarios.length) {
      idsDisponibles.add(uid); // ✅ Solo si está en TODOS los slots
    }
  }
}


// 5. Asegurar que el enfermero actual esté en la lista
if (data.enfermero) idsDisponibles.add(data.enfermero);

// 6. Construir objeto para el <select>
const enfermerosFiltrados = {};
for (const doc of enfermerosSnap.docs) {
  const uid = doc.id;
  if (idsDisponibles.has(uid)) {
    const nombre = `${doc.data().nombre} ${doc.data().apellido || ""}`.trim();
    enfermerosFiltrados[uid] = nombre;
  }
}

this.enfermeros = enfermerosFiltrados;

// LOGS útiles
console.log("📅 Fecha:", fecha);
console.log("🕓 Hora inicio:", horaInicio);
console.log("⏱️ Duración:", duracionTotal);
console.log("🔢 Slots necesarios:", slotsNecesarios);
console.log("🧠 Disponibilidad total:", disponibilidad);
console.log("✅ Esteticistas disponibles:", Array.from(idsDisponibles));
console.table(this.enfermeros);


        // HoraFin
        const horaFin =
          evento.extendedProps.horaFin ||
          this.formatearHora24aAMPM(
            sumarMinutos(this.convertirHoraAMPMa24(hora), duracionTotal)
          );
        const horaDisplay =
          this.normalizarHoraParaPicker(data.hora || "") || data.hora;

        const esPersonal =
          data.tipoCita === "personal" ||
          data.estado === "personal" ||
          Boolean(data.notaPersonal);
        const tituloPersonal =
          (typeof data.title === "string" && data.title.trim()) ||
          (typeof data.titulo === "string" && data.titulo.trim()) ||
          (typeof data.motivo === "string" && data.motivo.trim()) ||
          "Asunto personal";
        const titulo = esPersonal
          ? tituloPersonal
          : `${data.paciente?.nombre || "Paciente"} ${
              data.paciente?.apellido || ""
            }`.trim();
        const notaPersonal = data.notaPersonal || data.descripcion || "";

        //  Cita seleccionada
        this.citaSeleccionada = {
          id: evento.id,
          title: titulo,
          start: data.fecha,
          hora: horaDisplay,
          horaFin,
          telefono: data.paciente?.telefono || "No disponible",
          email: data.paciente?.email || "No disponible",
          direccion: data.paciente?.direccion || "No Disponible",
          enfermero: this.enfermeros[enfermeroId] || "Sin asignar",
          enfermeroId,
          paymentIntentId: data.paymentIntentId || null,
          medicamentos: data.medicamentos || "Sin medicamentos",
          estado: esPersonal ? "personal" : data.estado || "pendiente",
          servicios,
          tipoCita: esPersonal ? "personal" : data.tipoCita || "",
          notaPersonal,
          
        };

        this.formCita = {
          fechaCita: data.fecha || "",
          hora: this.normalizarHoraParaPicker(data.hora || ""),
          enfermero: enfermeroId,
          duracion: duracionTotal,
          servicios,
          titulo: esPersonal ? titulo : "",
          notaPersonal,
        };
        const index = this.citas.findIndex(c => c.id === evento.id);
if (index !== -1) {
  this.citas[index] = {
    ...this.citas[index], // conserva otros campos
    start: data.fecha,
    hora: horaDisplay,
    horaFin,
    enfermeroId,
    enfermero: this.enfermeros[enfermeroId] || "Sin asignar",
    servicios,
    duracion: duracionTotal,
    estado: esPersonal ? "personal" : data.estado || "pendiente",
    tipoCita: esPersonal ? "personal" : data.tipoCita || "",
    title: titulo,
    notaPersonal,
  };
}

        //  Mostrar modal
        this.modalModo = "vista";
        this.mostrarModal = true;
        this.abrirModalCitas({ dateStr: evento.startStr });
      } catch (error) {
        console.error(" Error al abrir modal cita evento:", error);
        this.mostrarModal = false;
      }
    },

    cerrarModal() {
      this.mostrarModal = false;
      this.modalModo = "vista";
      this.citaExpandidaId = null;
    },

    toggleCitaDetalle(citaId) {
      this.citaExpandidaId =
        this.citaExpandidaId === citaId ? null : citaId;
    },

    etiquetaEstadoCita(cita) {
      return cita?.estado || "pendiente";
    },

    claseEstadoCita(cita) {
      const estado = cita?.estado || "pendiente";
      if (estado === "cancelada") return "bg-red-50 text-red-600";
      if (estado === "confirmada") {
        return cita.confirmacionCliente
          ? "bg-emerald-50 text-emerald-700"
          : "bg-amber-50 text-amber-700";
      }
      return "bg-amber-50 text-amber-700";
    },

    formatTime(timeString) {
      if (!timeString) return "Invalid Time";

      const raw = String(timeString).trim();
      if (!raw) return "Invalid Time";

      try {
        const hora24 = this.convertirHoraAMPMa24(raw);
        if (hora24) return `${hora24}:00`;

        if (raw.includes("T")) {
          const prTime = formatIsoPrTime(raw);
          if (prTime) return `${prTime}:00`;
        }

        throw new Error("Formato no soportado");
      } catch (e) {
        console.error("Invalid time format:", e);
        return "Invalid Time";
      }
    },

    normalizarFechaKey(valor) {
      if (!valor) return "";
      if (typeof valor === "string") {
        return valor.split("T")[0];
      }
      if (valor instanceof Date) {
        return valor.toISOString().split("T")[0];
      }
      if (typeof valor.toDate === "function") {
        return valor.toDate().toISOString().split("T")[0];
      }
      return "";
    },

    combinarFechaHoraPr(fecha, hora24) {
      if (!fecha || !hora24 || hora24 === "Invalid Time") return "";
      const partes = String(hora24).split(":");
      const horas = (partes[0] || "00").padStart(2, "0");
      const minutos = (partes[1] || "00").padStart(2, "0");
      return `${fecha}T${horas}:${minutos}:00`;
    },

    extraerFechaKeyPr(iso) {
      return formatIsoPrDateKey(iso) || String(iso || "").split("T")[0] || "";
    },

    formatDate2(dateValue, type) {
      if (!dateValue) return "";
      try {
        const iso =
          dateValue instanceof Date ? dateValue.toISOString() : String(dateValue);
        const fechaKey = formatIsoPrDateKey(iso);
        if (!fechaKey) return "";
        if (!type) return fechaKey;
        return formatIsoPrTime(iso, { hour12: true });
      } catch (e) {
        console.error("Invalid date:", e);
        return "Invalid Date";
      }
    },

    async actualizarCita(info) {
      try {
        const citaId = info.event.id;
        const startIsoUtc = info.event.start.toISOString();
        const fecha = this.extraerFechaKeyPr(startIsoUtc);
        const hora24 = formatIsoPrTime(startIsoUtc);
        const horaAmPm = this.formatearHora24aAMPM(hora24);
        const citaRef = doc(db, "citasGlobales", citaId);
        await updateDoc(citaRef, {
          fecha,
          hora: horaAmPm,
        });
        alert("Cita actualizada correctamente");
      } catch (e) {
        console.error("Error al actualizar:", e);
      }
    },

    async eliminarCita(citaId) {
      try {
        if (!citaId) {
          alert("No se puede eliminar: ID de cita no válido.");
          return;
        }

        const confirmar = confirm(
          "¿Estás seguro de que deseas eliminar esta cita? Esta acción no se puede deshacer."
        );

        if (!confirmar) {
          return;
        }

        const citaRef = doc(db, "citasGlobales", citaId);
        const citaSnap = await getDoc(citaRef);

        if (!citaSnap.exists()) {
          alert("La cita no existe en la base de datos.");
          return;
        }

        const citaData = citaSnap.data();

        // Si hay un payment intent, intentar reembolsar
        if (citaData.paymentIntentId) {
          try {
            const functions = getFunctions();
            const reembolsar = httpsCallable(functions, "reembolsarPago");
            await reembolsar({
              intentId: citaData.paymentIntentId,
              reason: "Cita eliminada por administrador",
            });
            console.log("Reembolso procesado correctamente");
          } catch (reembolsoError) {
            console.error("Error al procesar reembolso:", reembolsoError);
            alert(
              "Advertencia: No se pudo procesar el reembolso automáticamente. Verifique manualmente en Stripe."
            );
          }
        }

        // Eliminar la cita de Firestore
        await deleteDoc(citaRef);

        // Enviar SMS de notificación al paciente
        try {
          const functions = getFunctions();
          const sendSms = httpsCallable(functions, "sendSms");

          const paciente = citaData.paciente || {};
          const nombre = `${paciente.nombre || "Cliente"} ${
            paciente.apellido || ""
          }`.trim();

          let numero = paciente.telefono?.toString() || "";
          if (!numero.startsWith("+1")) numero = "+1" + numero;

          if (/^\+\d{10,15}$/.test(numero)) {
            const fechaFormateada = this.formatDate(citaData.fecha);
            const mensaje = `Estimado/a ${nombre}, lamentamos informarte que tu cita del ${fechaFormateada} a las ${citaData.hora} ha sido cancelada. Contactanos para reprogramar.\n\nNat Spa - Oasis de Paz y Relajación`;

            await sendSms({
              to: numero,
              message: mensaje,
            });
            console.log("SMS de cancelación enviado");
          }
        } catch (smsError) {
          console.error("Error al enviar SMS:", smsError);
        }

        alert("Cita eliminada correctamente.");
        this.mostrarModal = false;
        this.cargarCitas();
      } catch (error) {
        console.error("Error al eliminar la cita:", error);
        alert("Ocurrió un error al eliminar la cita.");
      }
    },

    async denegarCita(cita) {
      try {
        if (!cita || !cita.id) {
          alert("No hay cita válida seleccionada.");
          return;
        }

        const confirmar = confirm(
          "¿Estás seguro de que deseas denegar esta cita? Se cancelará y se notificará al paciente."
        );

        if (!confirmar) {
          return;
        }

        const citaRef = doc(db, "citasGlobales", cita.id);
        const citaSnap = await getDoc(citaRef);

        if (!citaSnap.exists()) {
          alert("La cita no existe en la base de datos.");
          return;
        }

        const citaData = citaSnap.data();

        // Actualizar estado a cancelada
        await updateDoc(citaRef, {
          estado: "cancelada",
          fechaCancelacion: new Date().toISOString(),
          motivoCancelacion: "Denegada por administrador",
        });

        // Si hay un payment intent, procesar reembolso
        if (citaData.paymentIntentId) {
          try {
            const functions = getFunctions();
            const reembolsar = httpsCallable(functions, "reembolsarPago");
            await reembolsar({
              intentId: citaData.paymentIntentId,
              reason: "Cita denegada por administrador",
            });
            console.log("Reembolso procesado correctamente");
          } catch (reembolsoError) {
            console.error("Error al procesar reembolso:", reembolsoError);
            alert(
              "Advertencia: No se pudo procesar el reembolso automáticamente. Verifique manualmente en Stripe."
            );
          }
        }

        // Enviar SMS de notificación
        try {
          const functions = getFunctions();
          const sendSms = httpsCallable(functions, "sendSms");

          const paciente = citaData.paciente || {};
          const nombre = `${paciente.nombre || "Cliente"} ${
            paciente.apellido || ""
          }`.trim();

          let numero = paciente.telefono?.toString() || "";
          if (!numero.startsWith("+1")) numero = "+1" + numero;

          if (/^\+\d{10,15}$/.test(numero)) {
            const fechaFormateada = this.formatDate(citaData.fecha);
            const mensaje = `Estimado/a ${nombre}, lamentamos informarte que no pudimos confirmar tu cita del ${fechaFormateada} a las ${citaData.hora}. Tu pago ha sido reembolsado. Contactanos para reprogramar.\n\nNat Spa - Oasis de Paz y Relajación`;

            await sendSms({
              to: numero,
              message: mensaje,
            });
            console.log("SMS de denegación enviado");
          }
        } catch (smsError) {
          console.error("Error al enviar SMS:", smsError);
        }

        alert("Cita denegada y reembolso procesado.");
        this.cargarCitas();
      } catch (error) {
        console.error("Error al denegar la cita:", error);
        alert("Ocurrió un error al denegar la cita.");
      }
    },
  },

  mounted() {
    this.cargarCitas();
    this.calendarOptions.eventClick = this.abrirModalCitaEvento;
  },

  watch: {
    "formCita.duracion": {
      handler(newVal) {
        if (Number(newVal) > 0) {
          this.recalcularDisponibilidad();
        }
      },
      immediate: false,
    },
  },
};
</script>

<style scoped>
/* Contenedor del calendario con tamaño fijo */
.calendar-container {
  width: 100%;
  margin: 0 auto;
  height: 720px;
}

/* Asegurarse de que el calendario ocupe todo el espacio del contenedor */
:deep(.fc) {
  height: 100% !important;
}

.bg-white {
  z-index: 60;
  position: relative;
}

.fixed {
  z-index: 50;
  /* Asegúrate de que esté sobre el calendario */
}

/* Ajustes para el modal */
.modal-dialog {
  max-width: 500px;
  margin: 1.75rem auto;
}

/* Ajustes responsive */
@media (max-width: 768px) {
  .calendar-container {
    height: 600px;
  }
}
</style>
<style>
.evento-pendiente {
  background-color: #2563eb !important;
  border-color: #2563eb !important;
  color: #ffffff !important;
  font-weight: 600;
}

.evento-confirmado-cliente {
  background-color: #2563eb !important;
  border-color: #2563eb !important;
  color: #ffffff !important;
  font-weight: 600;
}

.evento-confirmado-admin {
  background-color: #2563eb !important;
  border-color: #2563eb !important;
  color: #ffffff !important;
  font-weight: 600;
}

.evento-personal {
  background-color: #0f172a !important;
  border-color: #0f172a !important;
  color: #ffffff !important;
  font-weight: 600;
}

.horario-esteticista {
  opacity: 0.9 !important;
  border: 1px solid rgba(15, 23, 42, 0.08) !important;
}

.fc .fc-bg-event.horario-esteticista {
  opacity: 0.9 !important;
  filter: saturate(1.2);
  background-image: repeating-linear-gradient(
    135deg,
    rgba(255, 255, 255, 0.18) 0,
    rgba(255, 255, 255, 0.18) 6px,
    rgba(255, 255, 255, 0) 6px,
    rgba(255, 255, 255, 0) 12px
  );
}

.fc .fc-bg-event.intervalo-horario {
  opacity: 0.85 !important;
}
</style>
      colorHorarioDisponible: "#22c55e",
